import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ClassificationDetails, ClassificationResult, patternChip, selectPosturePhotoTileClassification } from './ClassificationDetails';

interface PostureClassificationOverlayProps {
    classification: ClassificationResult | null;
    isVisible: boolean;
    onClose: () => void;
}

const PostureClassificationOverlay: React.FC<PostureClassificationOverlayProps> = ({
    classification,
    isVisible,
    onClose
}) => {
    const [isMinimized, setIsMinimized] = useState(true);
    const isMobile = useIsMobile();

    if (!isVisible) return null;

    const displayClassification = selectPosturePhotoTileClassification(classification);

    const overlayContent = !classification ? (
            <div className="fixed bottom-0 left-0 right-0 z-[60] bg-background border-t shadow-xl sm:absolute sm:z-50 sm:bottom-0 sm:left-0 sm:right-0 sm:rounded-b-lg">
                <div className="w-full px-4 py-2 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground italic">Awaiting analysis data...</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-6 text-xs"
                    >
                        Hide
                    </Button>
                </div>
            </div>
    ) : null;

    const chip = displayClassification ? patternChip(displayClassification.pattern) : null;

    const minimizedContent = classification && isMinimized ? (
            <div className="fixed bottom-0 left-0 right-0 z-[60] bg-background border-t shadow-xl sm:absolute sm:z-50 sm:bottom-0 sm:left-0 sm:right-0 sm:rounded-b-lg">
                <div className="w-full px-4 py-2">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                            <div
                                className="flex items-center gap-3 cursor-pointer flex-1"
                                onClick={() => setIsMinimized(false)}
                            >
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    title="Expand classification"
                                >
                                    <ChevronUp className="h-5 w-5" />
                                </Button>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-bold hidden sm:inline">Classification:</span>
                                    <Badge variant="outline" className={chip ? `border ${chip.className}` : 'border'}>
                                        {chip?.label ?? '—'}
                                    </Badge>
                                    {displayClassification?.lateralized && displayClassification.lateralized !== 'Balanced' && (
                                        <Badge variant="outline" className="border border-border bg-muted">
                                            {displayClassification.lateralized}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="h-6 text-xs"
                        >
                            Hide
                        </Button>
                    </div>
                </div>
            </div>
    ) : null;

    const expandedContent = classification && !isMinimized ? (
        <div className="fixed inset-0 z-[60] bg-background flex flex-col overflow-hidden border-0 sm:border sm:rounded-lg sm:absolute sm:z-50 sm:inset-0 shadow-xl">
            <div className="flex items-center justify-between p-4 border-b bg-background shrink-0">
                <h3 className="font-semibold">Posture Classification</h3>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsMinimized(true)}
                        className="h-8 w-8 p-0"
                        title="Minimize"
                    >
                        <ChevronDown className="h-5 w-5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 px-2 text-xs"
                    >
                        Close
                    </Button>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                <ClassificationDetails classification={displayClassification} showDetailSections />
            </div>
        </div>
    ) : null;

    const content = overlayContent ?? minimizedContent ?? expandedContent;
    if (!content) return null;

    // On mobile, render via portal to body so overlay is above BottomNav and not clipped
    if (isMobile && typeof document !== 'undefined' && document.body) {
        return createPortal(content, document.body);
    }
    return content;
};

export default PostureClassificationOverlay;
